import { jsPDF } from "jspdf";
import { num, eur } from "./format";

// Brand + neutral colors (RGB)
const BRAND = [13, 110, 130];
const SLATE = [51, 65, 85];
const MUTED = [100, 116, 139];

// jsPDF's built-in fonts can't render Greek glyphs, so we embed DejaVuSans
// (served from /public). The base64 is fetched once and reused.
let fontBase64Promise;
function loadFontBase64() {
  if (!fontBase64Promise) {
    fontBase64Promise = fetch("/DejaVuSans.ttf")
      .then((r) => {
        if (!r.ok) throw new Error("Δεν φορτώθηκε η γραμματοσειρά");
        return r.arrayBuffer();
      })
      .then((buf) => {
        const bytes = new Uint8Array(buf);
        let binary = "";
        const chunk = 0x8000;
        for (let i = 0; i < bytes.length; i += chunk) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
        }
        return btoa(binary);
      });
  }
  return fontBase64Promise;
}

// The bundled logo is a white fish on a dark square. For the teal PDF header we
// key out the dark background (→ transparent) so only the white fish remains.
let logoPromise;
function loadWhiteFishLogo() {
  if (!logoPromise) {
    logoPromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const d = imageData.data;
          for (let i = 0; i < d.length; i += 4) {
            if (d[i] < 90 && d[i + 1] < 90 && d[i + 2] < 90) d[i + 3] = 0; // dark → transparent
          }
          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = "/logo192x192.png";
    });
  }
  return logoPromise;
}

const money = (v) => eur(v);

const formatDate = (d) => {
  const date = new Date(d);
  return isNaN(date.getTime())
    ? String(d ?? "")
    : date.toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/**
 * Build a clean, generated PDF for one εκκαθάριση and open the native share
 * sheet (WhatsApp, email, …). Falls back to a plain download when the browser
 * can't share files (e.g. most desktops). Draws everything with the jsPDF API
 * (vector text/lines), not an HTML screenshot.
 */
export async function shareEkkatharisiPdf(data) {
  const {
    date,
    supplierName,
    promitheia,
    kivotia,
    somateio,
    etanal,
    metafora,
    diafora,
    trapeza,
    comments,
    fishes = [],
    emporevma,
    comm,
    ekkatharisi,
  } = data;

  const doc = new jsPDF({ unit: "pt", format: "a4" });

  const fontB64 = await loadFontBase64();
  doc.addFileToVFS("DejaVuSans.ttf", fontB64);
  doc.addFont("DejaVuSans.ttf", "DejaVuSans", "normal");
  doc.setFont("DejaVuSans", "normal");

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40;
  const right = pageW - M;
  let y = 0;

  const ensure = (space) => {
    if (y + space > pageH - M) {
      doc.addPage();
      y = M;
    }
  };

  const row = (label, value) => {
    ensure(20);
    doc.setFontSize(11);
    doc.setTextColor(...MUTED);
    doc.text(label, M, y);
    doc.setTextColor(...SLATE);
    doc.text(String(value ?? ""), right, y, { align: "right" });
    y += 18;
  };

  // --- Header band ---
  const logo = await loadWhiteFishLogo();
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageW, 72, "F");
  let textX = M;
  if (logo) {
    try {
      doc.addImage(logo, "PNG", M, 18, 38, 38);
      textX = M + 48;
    } catch {
      /* ignore logo failure */
    }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text("ΛΙΝΟΣ", textX, 36);
  doc.setFontSize(12);
  doc.text("Εκκαθάριση", textX, 56);
  doc.text(formatDate(date), right, 56, { align: "right" });
  y = 100;

  // --- Info ---
  row("Προμηθευτής", supplierName);
  row("Προμήθεια", `${num(promitheia)}%`);
  if (Number(kivotia) > 0) row("Κιβώτια", kivotia);

  y += 4;
  doc.setDrawColor(220);
  doc.line(M, y, right, y);
  y += 20;

  // --- Fish ---
  const colQty = right - 200;
  const colPrice = right - 110;
  const colTotal = right;

  fishes.forEach((fish) => {
    const qtyRows = (fish.quantities || []).length;
    ensure(30 + qtyRows * 14 + 40);

    doc.setFontSize(13);
    doc.setTextColor(...BRAND);
    doc.text(String(fish.name || ""), M, y);
    if (Number(fish.kivotia) > 0) {
      doc.setFontSize(10);
      doc.setTextColor(...MUTED);
      doc.text(`${fish.kivotia} Κιβώτια`, right, y, { align: "right" });
    }
    y += 16;

    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("Ποσ.", colQty, y, { align: "right" });
    doc.text("Τιμή", colPrice, y, { align: "right" });
    doc.text("Σύνολο", colTotal, y, { align: "right" });
    y += 13;

    doc.setFontSize(11);
    doc.setTextColor(...SLATE);
    let subtotal = 0;
    (fish.quantities || []).forEach((q) => {
      const qty = Number(q.qty) || 0;
      const price = Number(q.price) || 0;
      subtotal += qty * price;
      doc.text(num(qty), colQty, y, { align: "right" });
      doc.text(money(price), colPrice, y, { align: "right" });
      doc.text(money(qty * price), colTotal, y, { align: "right" });
      y += 14;
    });

    doc.setDrawColor(220);
    doc.line(colQty - 20, y - 3, colTotal, y - 3);
    doc.setFontSize(11);
    doc.setTextColor(...SLATE);
    doc.text(`Σύνολο: ${money(subtotal)}`, colTotal, y + 10, { align: "right" });
    doc.setTextColor(...MUTED);
    doc.text(`Προμήθεια: ${money((subtotal * (Number(promitheia) || 0)) / 100)}`, colTotal, y + 24, { align: "right" });
    y += 38;
  });

  // --- Other costs ---
  const hasCosts = [somateio, etanal, metafora, diafora].some((v) => Number(v) > 0);
  if (hasCosts) {
    y += 2;
    doc.setDrawColor(220);
    doc.line(M, y, right, y);
    y += 20;
    if (Number(somateio) > 0) row("Σωματείο", money(somateio));
    if (Number(etanal) > 0)
      row("ΕΤΑΝΑΛ", `${num(etanal)}% - ${money(((Number(emporevma) || 0) * (Number(etanal) || 0)) / 100)}`);
    if (Number(metafora) > 0) row("Μεταφορά", money(metafora));
    if (Number(diafora) > 0) row("Άλλα έξοδα", money(diafora));
  }

  // --- Totals box: clearing breakdown + bank → cash ---
  const metrita = (Number(ekkatharisi) || 0) - (Number(trapeza) || 0);
  ensure(130);
  y += 10;
  const boxW = right - M;
  const boxH = 112;
  doc.setFillColor(...BRAND);
  doc.roundedRect(M, y, boxW, boxH, 8, 8, "F");
  doc.setTextColor(255, 255, 255);

  const third = boxW / 3;
  const c3 = (i) => M + third * i + third / 2;
  doc.setFontSize(9);
  doc.text("ΕΜΠΟΡΕΥΜΑ", c3(0), y + 22, { align: "center" });
  doc.text("ΠΡΟΜΗΘΕΙΑ", c3(1), y + 22, { align: "center" });
  doc.text("ΕΚΚΑΘΑΡΙΣΗ", c3(2), y + 22, { align: "center" });
  doc.setFontSize(13);
  doc.text(money(emporevma), c3(0), y + 42, { align: "center" });
  doc.text(money(comm), c3(1), y + 42, { align: "center" });
  doc.setFontSize(15);
  doc.text(money(ekkatharisi), c3(2), y + 43, { align: "center" });

  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.5);
  doc.line(M + 16, y + 58, M + boxW - 16, y + 58);
  doc.setLineWidth(1);

  const half = boxW / 2;
  const c2 = (i) => M + half * i + half / 2;
  doc.setFontSize(9);
  doc.text("ΤΡΑΠΕΖΑ", c2(0), y + 78, { align: "center" });
  doc.text("ΜΕΤΡΗΤΑ", c2(1), y + 78, { align: "center" });
  doc.setFontSize(13);
  doc.text(`-${money(trapeza)}`, c2(0), y + 98, { align: "center" });
  doc.setFontSize(17);
  doc.text(money(metrita), c2(1), y + 99, { align: "center" });
  y += boxH + 16;

  // --- Comments ---
  if (comments) {
    ensure(40);
    doc.setFontSize(11);
    doc.setTextColor(...MUTED);
    doc.text("Σχόλια:", M, y);
    y += 14;
    doc.setTextColor(...SLATE);
    doc.text(doc.splitTextToSize(String(comments), right - M), M, y);
  }

  // ASCII-only filename — Greek characters get mangled by the OS/browser download layer.
  const filename = `LINOS-EKKATHARISI-${date}.pdf`;
  const blob = doc.output("blob");
  const file = new File([blob], filename, { type: "application/pdf" });

  // Prefer the native share sheet on mobile; fall back to a download.
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: `Εκκαθάριση ${formatDate(date)}`,
        text: `Εκκαθάριση ${formatDate(date)}`,
      });
      return;
    } catch (err) {
      // User cancelled the share sheet → don't also download.
      if (err && err.name === "AbortError") return;
      // Any other failure → fall through to download.
    }
  }

  doc.save(filename);
}
